import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Logo } from "./Logo";
import { NAV } from "../../data/firm";

const slug = (l) => l.toLowerCase().replace(/[^a-z]+/g, "-");

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 30);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,padding,border-color] duration-500 border-b ${
        scrolled ? "bg-white/90 backdrop-blur-xl py-3 border-ink/10" : "bg-transparent py-5 border-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo-link"><Logo /></Link>
        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-${slug(n.label)}-link`}
              className={({ isActive }) =>
                `relative px-4 py-2 text-sm font-medium transition-colors duration-300 ${isActive ? "text-[var(--brass-deep)]" : "text-ink/75 hover:text-ink"}`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>
        <Link
          to="/contact"
          data-testid="nav-cta-button"
          className="hidden lg:inline-flex group items-center gap-2 rounded-full bg-ink text-paper px-5 py-2.5 text-sm font-semibold hover:bg-brass hover:text-paper transition-colors duration-300"
        >
          Book a consultation
          <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:rotate-45" />
        </Link>
        <button
          data-testid="mobile-menu-button"
          onClick={() => setOpen((o) => !o)}
          className="lg:hidden text-ink p-2 -mr-2"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden absolute top-full inset-x-0 bg-white border-b border-ink/10 px-5 pb-8 pt-4 h-[calc(100vh-64px)]"
          >
            {NAV.map((n, i) => (
              <motion.div key={n.to} initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 * i }}>
                <NavLink
                  to={n.to}
                  end={n.to === "/"}
                  data-testid={`mobile-nav-${slug(n.label)}-link`}
                  className={({ isActive }) => `block py-3 border-b border-ink/10 font-display text-3xl font-bold ${isActive ? "text-[var(--brass-deep)]" : "text-ink"}`}
                >
                  {n.label}
                </NavLink>
              </motion.div>
            ))}
            <Link to="/contact" data-testid="mobile-nav-cta" className="mt-8 inline-flex items-center gap-2 rounded-full bg-ink text-paper px-6 py-3 font-semibold">
              Book a consultation <ArrowUpRight className="w-4 h-4" />
            </Link>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
